import { Alert } from "@/components/Alert";
import Shimmer from "@/components/Shimmer";
import { KlaviyoListsTable } from "@/components/Table";
import { H3, P } from "@/components/Typography";
import { useTracking } from "@/requests/hooks/app";
import { useGetDatashipsKlaviyoLists } from "@/requests/hooks/collect";
import { useUser } from "@/requests/hooks/user";
import { doesUserHaveScope } from "@/utils/doesUserHaveScope";
import { IFeatureFlagKeys } from "@/utils/featureFlags";
import { faList } from "@fortawesome/pro-duotone-svg-icons";
import { useFlags } from "flagsmith/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const KlaviyoLists = () => {
  const { t } = useTranslation();
  const { mutate: sendTracking } = useTracking();
  const { data: user } = useUser();
  const flags = useFlags<IFeatureFlagKeys>([
    "klaviyo-list-v2-endpoint-enabled",
  ]);
  const isKlaviyoListV2Enabled =
    flags?.["klaviyo-list-v2-endpoint-enabled"]?.enabled;
  const {
    data: datashipsKlaviyoLists,
    isLoading: isDatashipsKlaviyoListsLoading,
    isError: isDatashipsKlaviyoListsError,
  } = useGetDatashipsKlaviyoLists(isKlaviyoListV2Enabled);

  useEffect(() => {
    sendTracking("control-panel.email.integration-settings.klaviyo-lists.view");
  }, []);

  const getHasMissingList = () => {
    const hasMissingV1List = (
      datashipsKlaviyoLists as IDatashipsKlaviyoList[]
    ).some(({ name }) => name === null);

    const hasMissingV2List =
      (datashipsKlaviyoLists as IGetDatashipsKlaviyoLists).find(
        ({ lists, channel }) => {
          if (
            channel === "SMS" &&
            doesUserHaveScope("LIST_SMS_CONTACTS", user?.scopes)
          )
            return lists?.find(({ name }) => name === null);

          if (channel === "EMAIL")
            return lists?.find(({ name }) => name === null);
        },
      ) || isDatashipsKlaviyoListsError;

    return (
      (isKlaviyoListV2Enabled && !hasMissingV2List) ||
      (!isKlaviyoListV2Enabled && !hasMissingV1List)
    );
  };

  const renderHeading = () => (
    <div className="max-w-md">
      <div className="mb-10">
        <H3 className="font-bold">
          {t("settings.integration.klaviyoLists.title")}
        </H3>
      </div>
      <P>{t("settings.integration.klaviyoLists.description")}</P>
    </div>
  );

  const renderMissingAlertContent = () => (
    <div className="max-w-md">
      <div className="mb-10">
        <H3 className="font-bold">
          {t("settings.integration.klaviyoLists.missingList.title")}
        </H3>
      </div>
      <P>{t("settings.integration.klaviyoLists.missingList.description")}</P>
    </div>
  );

  const renderMissingListAlert = () => {
    if (!datashipsKlaviyoLists || isDatashipsKlaviyoListsLoading) return null;

    const hasMissingList = getHasMissingList();

    if (hasMissingList) return null;

    return (
      <div className="mx-0 my-30">
        <Alert
          variant="error"
          titleSlot={renderMissingAlertContent()}
          icon={faList}
          iconClassName="h-30 w-30"
        />
      </div>
    );
  };

  const renderLoading = () => (
    <>
      <Shimmer height="234px" rounded="sm" />
      {doesUserHaveScope("LIST_SMS_CONTACTS", user?.scopes) && (
        <div className="mt-30">
          <Shimmer height="234px" rounded="sm" />
        </div>
      )}
    </>
  );

  const renderTables = () => {
    if (isDatashipsKlaviyoListsLoading) {
      return renderLoading();
    }

    if (!isKlaviyoListV2Enabled) {
      const structuredLists: IDatashipsChannelLists = {
        channel: "EMAIL",
        lists: datashipsKlaviyoLists as IDatashipsKlaviyoList[],
      };
      return <KlaviyoListsTable klaviyoLists={structuredLists} />;
    }

    if (doesUserHaveScope("LIST_SMS_CONTACTS", user?.scopes)) {
      return (
        <div className="flex flex-grow flex-col gap-40">
          {(datashipsKlaviyoLists as IGetDatashipsKlaviyoLists)?.map(
            (datashipsKlaviyoList, i) => (
              <KlaviyoListsTable key={i} klaviyoLists={datashipsKlaviyoList} />
            ),
          )}
        </div>
      );
    }

    const emailList = (
      datashipsKlaviyoLists as IGetDatashipsKlaviyoLists
    )?.find(list => list.channel === "EMAIL");

    return <KlaviyoListsTable klaviyoLists={emailList} />;
  };

  return (
    <div className="mt-50">
      {renderHeading()}
      {renderMissingListAlert()}
      <div className="mt-30">{renderTables()}</div>
    </div>
  );
};

export default KlaviyoLists;
